import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import { apiRequest, createUserCart } from '../utils/data';
import env from '../utils/env';

const GROWTH = {
  FETCH_USER_SUBSCRIPTIONS_REQUEST: 'growth/fetch_user_subscriptions_request',
  FETCH_USER_SUBSCRIPTIONS_SUCCESS: 'growth/fetch_user_subscriptions_success',
  FETCH_USER_SUBSCRIPTIONS_FAILURE: 'growth/fetch_user_subscriptions_failure',
  SELECT_PRODUCT_DETAIL: 'growth/select_product_detail',
  UNSELECT_PRODUCT_DETAIL: 'growth/unselect_product_detail',
  CANCEL_USER_SUBSCRIPTION_REQUEST: 'growth/cancel_user_subscription_request',
  CANCEL_USER_SUBSCRIPTION_SUCCESS: 'growth/cancel_user_subscription_success',
  CANCEL_USER_SUBSCRIPTION_FAILURE: 'growth/cancel_user_subscription_failure',
  UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_REQUEST:
    'growth/update_product_subscription_quantity_request',
  UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_SUCCESS:
    'growth/update_product_subscription_quantity_success',
  UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_FAILURE:
    'growth/update_product_subscription_quantity_failure',
  CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_REQUEST:
    'growth/create_instant_cart_for_box_selections_request',
  CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_SUCCESS:
    'growth/create_instant_cart_for_box_selections_success',
  CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_FAILURE:
    'growth/create_instant_cart_for_box_selections_failure',
};

const initialState = {
  userSubscriptions: [],
  isLoading: false,
  error: null,
  selectedProduct: {},
  cancelingSubscriptionId: null,
  isCanceling: false,
  cancelError: null,
  updatingSubscriptionId: null,
  isUpdating: false,
  updateError: null,
  isCreatingInstantCartForBoxSelections: false,
  createInstantCartForBoxSelectionsError: null,
};

export const fetchUserSubscriptionsRequest = () => ({
  type: GROWTH.FETCH_USER_SUBSCRIPTIONS_REQUEST,
});

export const fetchUserSubscriptionsSuccess = (userSubscriptions) => ({
  type: GROWTH.FETCH_USER_SUBSCRIPTIONS_SUCCESS,
  userSubscriptions,
});

export const fetchUserSubscriptionsFailure = (error) => ({
  type: GROWTH.FETCH_USER_SUBSCRIPTIONS_FAILURE,
  error,
});

export const selectProductDetail = (product) => ({
  type: GROWTH.SELECT_PRODUCT_DETAIL,
  product,
});

export const unselectProductDetail = () => ({
  type: GROWTH.UNSELECT_PRODUCT_DETAIL,
});

export const cancelUserSubscriptionRequest = (subscriptionUuid) => ({
  type: GROWTH.CANCEL_USER_SUBSCRIPTION_REQUEST,
  subscriptionUuid,
});

export const cancelUserSubscriptionSuccess = (subscriptionUuid) => ({
  type: GROWTH.CANCEL_USER_SUBSCRIPTION_SUCCESS,
  subscriptionUuid,
});

export const cancelUserSubscriptionFailure = (error) => ({
  type: GROWTH.CANCEL_USER_SUBSCRIPTION_FAILURE,
  error,
});

export const updateProductSubscriptionQuantityRequest = (subscriptionUuid) => ({
  type: GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_REQUEST,
  subscriptionUuid,
});

export const updateProductSubscriptionQuantitySuccess = (subscriptionUuid, quantity) => ({
  type: GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_SUCCESS,
  subscriptionUuid,
  quantity,
});

export const updateProductSubscriptionQuantityFailure = (error) => ({
  type: GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_FAILURE,
  error,
});

export const createInstantCartForBoxSelectionsRequest = () => ({
  type: GROWTH.CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_REQUEST,
});

export const createInstantCartForBoxSelectionsSuccess = (cartUuid) => ({
  type: GROWTH.CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_SUCCESS,
  cartUuid,
});

export const createInstantCartForBoxSelectionsFailure = (error) => ({
  type: GROWTH.CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_FAILURE,
  error,
});

export const getUserSubscriptions = () => async (dispatch) => {
  dispatch(fetchUserSubscriptionsRequest());
  try {
    const response = await fetch(
      apiRequest('GET', `${env.growth}/v1/subscriptions/self?unCurlEnabled=true`),
    );
    if (response.status >= 300) {
      throw response.status;
    } else {
      const data = await response.json();
      dispatch(fetchUserSubscriptionsSuccess(data.subscriptions || []));
    }
  } catch (error) {
    dispatch(fetchUserSubscriptionsFailure(error));
  }
};

export const cancelUserSubscription = (subscriptionUuid) => async (dispatch) => {
  dispatch(cancelUserSubscriptionRequest(subscriptionUuid));
  try {
    const response = await fetch(
      apiRequest(
        'PATCH',
        `${env.growth}/v1/subscriptions/self/subscription-uuid/${subscriptionUuid}/cancel`,
      ),
    );
    if (response.status >= 300) {
      throw response.status;
    } else {
      dispatch(cancelUserSubscriptionSuccess(subscriptionUuid));
      // TODO: remove this call to getUserSubscriptions if cancelUserSubscriptionSuccess updates the store correctly
      dispatch(getUserSubscriptions());
    }
  } catch (error) {
    dispatch(cancelUserSubscriptionFailure(error));
  }
};

export const updateSubscriptionQuantity = (payload) => async (dispatch) => {
  const { subscriptionUuid, quantity } = payload;
  dispatch(updateProductSubscriptionQuantityRequest(subscriptionUuid));
  try {
    const response = await fetch(
      apiRequest(
        'PATCH',
        `${env.growth}/v1/subscriptions/self/subscription-uuid/${subscriptionUuid}/quantity`,
        {
          quantity,
        },
      ),
    );
    if (response.status >= 300) {
      throw response.status;
    } else {
      dispatch(updateProductSubscriptionQuantitySuccess(subscriptionUuid, quantity));
      showSuccessToast(
        'reFills Updated',
        'Your quantity has been successfully updated to all future deliveries.',
      );
    }
  } catch (error) {
    dispatch(updateProductSubscriptionQuantityFailure(error));
    showErrorToast(
      'Update Failed',
      'There was an issue updating your quantity. Please try again later.',
    );
  }
};

// Choice types that are safe to POST to the ACE INSTANT cart. Mirrors maui's
// `submittableSelectionChoiceTypes` in `_ui/components/CustomizeFlow/utils.ts`
// used by `buildRecurringSelectionsCartItemsFromSelectionMap` — the maui path
// this handoff must match. INSERT selections are server-managed (free gifts,
// sponsored inserts) and must NOT appear in the payload; sending them creates
// duplicated / invalid instant carts for boxes that have inserts.
const SUBMITTABLE_CHOICE_TYPES = new Set(['PRIMARY', 'ADDITIONAL']);

/**
 * Creates a fresh INSTANT cart from a subscription's customize selections and
 * returns the new `cartUuid` so the caller can redirect to maui's instant
 * checkout (see `openMauiInstantCheckout`).
 *
 * Named generically (not `…ForBeautyEditSelections`) because the write is
 * box-type-agnostic — the payload depends only on which selections are
 * ADDITIONAL / luxe PRIMARY, not on `customizeSaleType`. Seasonal box / pup box
 * consumers can reuse this thunk once their flows land.
 *
 * INSERT selections are filtered out before the POST because they are managed
 * by the backend (free gifts, sponsored inserts) and must not appear in the
 * ACE create-cart payload. This matches maui's recurring instant-checkout path
 * which limits the submittable set to PRIMARY + ADDITIONAL via
 * `submittableSelectionChoiceTypes`. Beyond that filter the thunk does NOT
 * filter by paid-ness — the caller decides when to invoke it (only when the
 * subscription has paid selections). It also does NOT refresh
 * `userExistingCart`; INSTANT carts are independent of the user's CUSTOMER and
 * CUSTOMIZE carts and their `prepaidSubscriptionGroups` are surfaced on the
 * INSTANT cart itself when the user lands on checkout.
 * @param {object} params
 * @param {Array<{
 *   productUuid: string,
 *   saleUuid: string,
 *   catalogUuid: string,
 *   choiceType: 'PRIMARY' | 'ADDITIONAL' | 'INSERT',
 *   quantity?: number,
 * }>} params.selections - The full customize-selection set for the sale.
 *   INSERT entries are filtered out before the POST; PRIMARY + ADDITIONAL are
 *   forwarded so the backend can attribute primary picks to the subscription
 *   while the user pays for the surcharges.
 * @returns {Promise<string | null>} - The new `cartUuid` on success, `null` on failure.
 */
export const createInstantCartForBoxSelections =
  ({ selections }) =>
  async (dispatch) => {
    dispatch(createInstantCartForBoxSelectionsRequest());

    try {
      const validSelections = (selections || []).filter((selection) =>
        Boolean(selection.productUuid),
      );

      if (validSelections.some((selection) => !selection.choiceType)) {
        throw new Error(
          'Cannot create instant cart: one or more selections are missing choiceType',
        );
      }

      const submittableSelections = validSelections.filter((selection) =>
        SUBMITTABLE_CHOICE_TYPES.has(selection.choiceType),
      );

      const cartItems = submittableSelections.map((selection) => ({
        productUuid: selection.productUuid,
        quantity: selection.quantity > 0 ? selection.quantity : 1,
        saleUuid: selection.saleUuid,
        catalogUuid: selection.catalogUuid,
        cartItemPreferences: {
          CUSTOMIZE_CHOICE_TYPE: selection.choiceType,
        },
      }));

      const response = await createUserCart('INSTANT', cartItems);

      if (response?.error || response?.success === false) {
        throw response?.error ?? new Error('Failed to create instant cart');
      }

      const newCartUuid = response?.data?.items?.[0]?.uuid;
      if (!newCartUuid) {
        throw new Error('Instant cart created but no cartUuid found in response');
      }

      dispatch(createInstantCartForBoxSelectionsSuccess(newCartUuid));
      return newCartUuid;
    } catch (error) {
      dispatch(createInstantCartForBoxSelectionsFailure(error));
      showErrorToast(
        'Could not start checkout',
        'There was an issue starting your checkout. Please try again later.',
      );
      return null;
    }
  };

const growthReducer = (state = initialState, action) => {
  switch (action.type) {
    case GROWTH.FETCH_USER_SUBSCRIPTIONS_REQUEST:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case GROWTH.FETCH_USER_SUBSCRIPTIONS_SUCCESS:
      return {
        ...state,
        isLoading: false,
        userSubscriptions: action.userSubscriptions,
        error: null,
      };

    case GROWTH.FETCH_USER_SUBSCRIPTIONS_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };

    case GROWTH.SELECT_PRODUCT_DETAIL:
      return {
        ...state,
        selectedProduct: {
          ...action.product,
        },
      };

    case GROWTH.UNSELECT_PRODUCT_DETAIL:
      return {
        ...state,
        selectedProduct: {},
      };

    case GROWTH.CANCEL_USER_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        cancelingSubscriptionId: action.subscriptionUuid,
        isCanceling: true,
        cancelError: null,
      };

    case GROWTH.CANCEL_USER_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        cancelingSubscriptionId: null,
        isCanceling: false,
        cancelError: null,
        userSubscriptions: state.userSubscriptions.map((subscription) =>
          subscription.id === action.subscriptionUuid
            ? { ...subscription, cancelled: true, active: false }
            : subscription,
        ),
      };

    case GROWTH.CANCEL_USER_SUBSCRIPTION_FAILURE:
      return {
        ...state,
        cancelingSubscriptionId: null,
        isCanceling: false,
        cancelError: action.error,
      };

    case GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_REQUEST:
      return {
        ...state,
        updatingSubscriptionId: action.subscriptionUuid,
        isUpdating: true,
        updateError: null,
      };

    case GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_SUCCESS:
      return {
        ...state,
        updatingSubscriptionId: null,
        isUpdating: false,
        updateError: null,
        userSubscriptions: state.userSubscriptions.map((subscription) =>
          subscription.id === action.subscriptionUuid
            ? { ...subscription, quantity: action.quantity }
            : subscription,
        ),
      };

    case GROWTH.UPDATE_PRODUCT_SUBSCRIPTION_QUANTITY_FAILURE:
      return {
        ...state,
        updatingSubscriptionId: null,
        isUpdating: false,
        updateError: action.error,
      };

    case GROWTH.CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_REQUEST:
      return {
        ...state,
        isCreatingInstantCartForBoxSelections: true,
        createInstantCartForBoxSelectionsError: null,
      };

    case GROWTH.CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_SUCCESS:
      return {
        ...state,
        isCreatingInstantCartForBoxSelections: false,
        createInstantCartForBoxSelectionsError: null,
      };

    case GROWTH.CREATE_INSTANT_CART_FOR_BOX_SELECTIONS_FAILURE:
      return {
        ...state,
        isCreatingInstantCartForBoxSelections: false,
        createInstantCartForBoxSelectionsError: action.error,
      };

    default:
      return state;
  }
};

export default growthReducer;
